"use client";

import { Hammer, Lightbulb, Puzzle, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SpecializedServices() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const features = [{
    icon: Hammer,
    title: "Precision Retrofitting",
    description: "We breathe new life into legacy CNC machines using state-of-the-art Siemens Sinumerik systems for unmatched accuracy."
  }, {
    icon: Lightbulb,
    title: "Siemens Integration",
    description: "Specialized expertise in Siemens automation, ensuring seamless communication between hardware and software layers."
  }, {
    icon: Puzzle,
    title: "Custom Field Services",
    description: "On-site diagnostics and repairs tailored to your specific industrial environment, minimizing downtime and maximizing output."
  }, {
    icon: Sparkles,
    title: "Certified Spare Parts",
    description: "Direct access to genuine Siemens components and high-quality alternatives for Heidenhain and Fanuc systems."
  }];
  return <section data-section-id="1550" id="specialized-services" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32">
      {}
      <svg aria-hidden="true" className="absolute -top-px left-0 w-full h-6 text-background rotate-180" viewBox="0 0 1440 24" preserveAspectRatio="none">
        <path d="M0,0 L0,12 L40,18 L90,8 L140,20 L200,10 L260,22 L320,8 L380,18 L450,6 L520,20 L590,10 L660,22 L730,8 L800,18 L870,6 L940,20 L1010,10 L1080,22 L1150,8 L1220,18 L1290,6 L1360,20 L1440,12 L1440,0 Z" fill="currentColor" />
      </svg>

      {}
      <svg aria-hidden="true" className="hidden md:block absolute top-24 right-12 w-14 h-14 text-primary opacity-20" viewBox="0 0 60 60">
        <g stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none">
          <line x1="30" y1="6" x2="30" y2="18" />
          <line x1="30" y1="42" x2="30" y2="54" />
          <line x1="6" y1="30" x2="18" y2="30" />
          <line x1="42" y1="30" x2="54" y2="30" />
          <line x1="13" y1="13" x2="22" y2="22" />
          <line x1="38" y1="38" x2="47" y2="47" />
        </g>
      </svg>
      <svg aria-hidden="true" className="hidden md:block absolute bottom-32 left-10 w-12 h-12 text-secondary opacity-30" viewBox="0 0 50 50">
        <path d="M25,5 L29,20 L44,22 L32,32 L36,46 L25,38 L14,46 L18,32 L6,22 L21,20 Z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      </svg>

      <div className="relative max-w-[1360px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className="lg:col-span-5 lg:sticky lg:top-24 self-start relative" style={{
          opacity: leftVisible ? 1 : 0,
          transform: leftVisible ? "translateY(0)" : "translateY(20px)",
          transition: "all 600ms cubic-bezier(0.16, 1, 0.3, 1)"
        }}>
            {}
            <div aria-hidden="true" className="absolute -top-10 -left-4 font-default text-[14rem] md:text-[18rem] leading-none pointer-events-none select-none opacity-5" style={{
            WebkitTextStroke: "1px hsl(183 100% 32%)",
            color: "transparent"
          }}>
              01
            </div>

            <div className="relative">
              {}
              <div className="inline-flex items-center gap-2 bg-primary text-primary-foreground border border-primary px-4 py-1.5 rounded-md font-mono text-xs tracking-widest uppercase font-medium" style={{
              transform: "rotate(0deg)"
            }}>Specialized Services</div>

              <h2 className="font-default mt-8 text-foreground font-bold" style={{
              fontSize: "clamp(40px, 8vw, 60px)",
              letterSpacing: "-0.04em",
              lineHeight: 1.1
            }}>
                Engineering the future of{" "}
                <span className="relative inline-block">
                  <svg aria-hidden="true" className="absolute -inset-3 w-[calc(100%+1.5rem)] h-[calc(100%+1.5rem)] -z-0" viewBox="0 0 160 100" preserveAspectRatio="none">
                    <rect x="10" y="30" width="140" height="40" fill="none" stroke="hsl(183 100% 32%)" strokeWidth="1" style={{
                    transform: "rotate(-1deg)",
                    transformOrigin: "center"
                  }} />
                  </svg>
                  <span className="relative z-10 text-primary">CNC</span>
                </span>
                .
              </h2>

              <p className="mt-8 text-muted-foreground max-w-xl font-default" style={{
              fontSize: "clamp(18px, 1.5vw, 19px)",
              lineHeight: 1.6
            }}>IMCNC Designs provides high-quality integration and retrofitting solutions. We focus on Siemens parts and services to ensure your industrial operations run with absolute precision and zero compromise.</p>

              <div className="mt-10">
                <Link to="/AboutPage">
                  <Button className="bg-primary text-primary-foreground border border-primary hover:opacity-90 rounded-md px-8 py-4 font-mono text-sm tracking-widest uppercase font-semibold h-auto">
                    Our Mission
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className="lg:col-span-7 flex flex-col">
            {features.map((feature, i) => {
            const Icon = feature.icon;
            const isLast = i === features.length - 1;
            return <div key={i} data-index={i} className="group relative" style={{
              opacity: rightVisible ? 1 : 0,
              transform: rightVisible ? "translateY(0)" : "translateY(20px)",
              transition: `all 600ms cubic-bezier(0.16, 1, 0.3, 1) ${i * 100}ms`
            }}>
                  <div className="grid grid-cols-[auto_1fr] gap-5 md:gap-7 py-8 md:py-10 border-l-2 border-transparent hover:border-primary transition-colors duration-0 pl-4">
                    {}
                    <div className="relative">
                      <div className="w-16 h-16 rounded-md border border-border bg-card flex items-center justify-center text-primary">
                        <Icon className="w-8 h-8" strokeWidth={1.5} />
                      </div>
                      {}
                      <svg aria-hidden="true" className="absolute -top-2 -right-2 w-5 h-5 text-primary" viewBox="0 0 24 24">
                        <g stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none">
                          <line x1="12" y1="3" x2="12" y2="8" />
                          <line x1="12" y1="16" x2="12" y2="21" />
                          <line x1="3" y1="12" x2="8" y2="12" />
                          <line x1="16" y1="12" x2="21" y2="12" />
                        </g>
                      </svg>
                    </div>

                    {}
                    <div>
                      <h3 className="font-default text-foreground inline-block relative font-bold" style={{
                    fontSize: "clamp(22px, 4vw, 25px)",
                    letterSpacing: "-0.02em",
                    lineHeight: 1.2
                  }}>
                        {feature.title}
                        <div className="absolute -left-6 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary opacity-0 group-hover:opacity-100 transition-opacity duration-0" />
                      </h3>
                      <p className="mt-4 text-muted-foreground font-default" style={{
                    fontSize: "19px",
                    lineHeight: 1.6
                  }}>
                        {feature.description}
                      </p>
                    </div>
                  </div>

                  {}
                  {!isLast && <div className="w-full h-px bg-border" />}
                </div>;
          })}
          </div>
        </div>
      </div>

      {}
      <svg aria-hidden="true" className="absolute -bottom-px left-0 w-full h-6 text-background" viewBox="0 0 1440 24" preserveAspectRatio="none">
        <path d="M0,0 L0,12 L40,18 L90,8 L140,20 L200,10 L260,22 L320,8 L380,18 L450,6 L520,20 L590,10 L660,22 L730,8 L800,18 L870,6 L940,20 L1010,10 L1080,22 L1150,8 L1220,18 L1290,6 L1360,20 L1440,12 L1440,0 Z" fill="currentColor" />
      </svg>
    </section>;
}